import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { translations } from './translations';

const LanguageContext = createContext(null);

const SUPPORTED_LANGUAGES = ['en', 'fr'];
const DEFAULT_LANGUAGE = 'en';
const STORAGE_KEY = 'tlr_language';

const normalizeLanguage = (value) => {
  if (value && SUPPORTED_LANGUAGES.includes(value)) return value;
  return DEFAULT_LANGUAGE;
};

const getInitialLanguage = () => {
  try {
    const fromStorage = localStorage.getItem(STORAGE_KEY);
    if (fromStorage) return normalizeLanguage(fromStorage);
  } catch {
    // ignore
  }

  if (typeof navigator !== 'undefined' && navigator.language) {
    const nav = navigator.language.toLowerCase();
    if (nav.startsWith('fr')) return 'fr';
  }

  return DEFAULT_LANGUAGE;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(getInitialLanguage);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, language);
    } catch {
      // ignore
    }

    if (typeof document !== 'undefined') {
      document.documentElement.lang = language;
      const dictionary = translations[language] || translations[DEFAULT_LANGUAGE];
      if (dictionary?.seo?.title) document.title = dictionary.seo.title;
      const description = document.querySelector('meta[name="description"]');
      if (description && dictionary?.seo?.description) {
        description.setAttribute('content', dictionary.seo.description);
      }
    }
  }, [language]);

  const value = useMemo(() => {
    const dictionary = translations[language] || translations[DEFAULT_LANGUAGE];

    const t = (path) => {
      const parts = String(path).split('.');
      let current = dictionary;
      for (const part of parts) {
        if (current && typeof current === 'object' && part in current) current = current[part];
        else return path;
      }
      return current;
    };

    return { language, setLanguage, t, dictionary };
  }, [language]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within a LanguageProvider');
  return ctx;
};
